// Б.1.b: каркас Модификаций (Синтетикс).
//
// Архитектура — «императивные дельты», как уже принято в проекте (Танаки-passive,
// Аура Второго директора): эффект применяется в момент attach, дельта сохраняется
// внутри объекта Mod и откатывается в detach. НЕ строим централизованный
// recomputeCardCurrent — это бы конфликтовало с существующими баффами Деуса.
//
// Структура объекта Mod:
//   {
//     id: 'syn_mod_mehanodendrit',
//     name_ru: 'Механодендрит',
//     source: { ownerIdx, sourceCardId } | null,
//     attachedAtTurn: number,
//     applied: {
//       atk, armor, maxHp, hpDelta,    // дельты, которые надо откатить
//       keywordsAdded: string[]        // только те, что МЫ добавили (без дублей)
//     }
//   }
//
// Реестр эффектов (MOD_REGISTRY) маршрутизирует id → handler. Незнакомый id
// пишет console.warn и не делает ничего (Мод всё равно попадает в массив,
// чтобы не рассинхронить логику Перегрузки).

import { hasKeyword, removeKeyword, KW } from '../../keywords.js';
import { onUnitDied } from '../deus/index.js';
import { shortHeroName } from '../../../data/heroes.js';
// Этап 56: статы/keywords Мода при навеске берутся СТРОГО из данных карты
// (xlsx = единственный источник правды). Раньше MOD_REGISTRY-хэндлеры хардкодили
// устаревшие значения (Механодендрит 2/1/2 вместо 2/0/0, Берсерк терял
// +2/Неистовство/Иммунитет и т.д.). Теперь хэндлеры зовутся ради ПОБОЧНЫХ
// эффектов (напр. Заводской имплант +1 к порогу Перегрузки), а стат/keyword-
// бонус перетирается значениями из шаблона карты.
import { cards as _cardsSyntetics } from '../../../data/cards-syntetics.js';
const MOD_TPL = new Map(_cardsSyntetics.filter(c => c.type === 'mod').map(c => [c.id, c]));
import { bishorOnAllyModified } from './bishor-trigger.js';
import { kiberPsihOnModAttached, kiberPsihOnModDetached } from './kiber-psih.js';
// Б.2.a: триггеры на смерть носителя и на конец хода.
import { batareyaOnDeath, MOD_ID as BATAREYA_ID } from './eksperimentalnaya-batareya.js';
import { vtoroeSerdtseOnDeath, MOD_ID as SERDTSE_ID } from './vtoroe-serdtse.js';
import { tsifrovoeZaveschanieEot, MOD_ID as ZAVESCHANIE_ID } from './tsifrovoe-zaveschanie.js';
import { vychMoschnostiEot, MOD_ID as MOSCHNOSTI_ID } from './vychislitelnye-moschnosti.js';
// Б.2.c: Хирург (триггер на розыгрыш Мода) и Подпольный боец (cantAttack-toggle).
import { hirurgOnModPlayed } from './hirurg.js';
import { podpolnyyOnModAttached, podpolnyyOnModDetached } from './podpolnyy-boets.js';
// Б.2.c: триггер «союзный персонаж получил урон» (Тёмный нетраннер) для Mod progression.
import { notifyCharacterDamaged } from './temnyy-netranner.js';
// C.1: общий реестр mod_played-триггеров (Доктор Шрам, Бишор-лич, и др).
import { fireOnModPlayed } from './c1/mod-played-triggers.js';
// C.1: late-binding хуков из mods-batch.js (избежание циклического импорта).
// var — для использования в TDZ-период при первом импорте.
// eslint-disable-next-line no-var
var _fireOnAllyReceivedMod = null;
// eslint-disable-next-line no-var
var _recomputeModCountBonus = null;
export function setModHooks({ fireOnAllyReceivedMod, recomputeModCountBonus } = {}) {
  if (typeof fireOnAllyReceivedMod === 'function') _fireOnAllyReceivedMod = fireOnAllyReceivedMod;
  if (typeof recomputeModCountBonus === 'function') _recomputeModCountBonus = recomputeModCountBonus;
}

// === Хелпер для отчёта владельца юнита в логе ===
function ownerName(state, card) {
  const idx = card?.ownerId;
  if (idx == null) return '?';
  return state.players[idx]?.name || '?';
}

// === Реестр эффектов Модов ===
//
// Каждый handler возвращает объект desiredEffect:
//   { atk, armor, maxHp, hpDelta, keywords: string[], description: string }
// attachMod применит дельты императивно, учитывая stealthUsed для 'stealth'.
const MOD_REGISTRY = {
  'syn_mod_mehanodendrit': () => ({
    atk: 2, armor: 1, maxHp: 2, hpDelta: 2,
    keywords: [],
    description: '+2/+1/+2'
  }),
  'syn_mod_ostry_vzglyad': () => ({
    atk: 0, armor: 0, maxHp: 0, hpDelta: 0,
    keywords: [KW.PIERCE],
    description: 'Пробитие'
  }),
  'syn_mod_kamuflyazh': () => ({
    atk: 0, armor: 0, maxHp: 0, hpDelta: 0,
    keywords: [KW.STEALTH],
    description: 'Скрытность'
  }),

  // Б.2.a: Перегруз рецепторов — добавляет Неистовство (FRENZY).
  // Сам эффект «атаковать дважды» реализован в phases.collectPhase3Attackers
  // (юнит с FRENZY добавляется в очередь Ф3 дважды).
  'syn_mod_peregruz': () => ({
    atk: 0, armor: 0, maxHp: 0, hpDelta: 0,
    keywords: [KW.FRENZY],
    description: 'Неистовство'
  }),

  // Б.2.a: Моды с триггерами на смерть/EOT — их attach не меняет статы,
  // эффекты обрабатываются в applyModDeathTriggers / applyModEndOfTurnTriggers.
  // Зато описание в логе attach информативно.
  'syn_mod_batareya': () => ({
    atk: 0, armor: 0, maxHp: 0, hpDelta: 0,
    keywords: [],
    description: 'Посмертие: 5 ударов по 1 (игнор Брони)'
  }),
  'syn_mod_vtoroe_serdtse': () => ({
    atk: 0, armor: 0, maxHp: 0, hpDelta: 0,
    keywords: [],
    description: 'Посмертие: возврат с 1 ХП в Ф1 след. хода'
  }),
  'syn_mod_zaveshanie': () => ({
    atk: 0, armor: 0, maxHp: 0, hpDelta: 0,
    keywords: [],
    description: 'EOT: гибель носителя, +50% его ХП Герою'
  }),
  'syn_ispolzovanie_vychislitelnyh_mo': () => ({
    atk: 0, armor: 0, maxHp: 0, hpDelta: 0,
    keywords: [],
    description: 'EOT: +1 случайный Мод из колоды в руку'
  })
};

const _warned = new Set();

// C.1: API для регистрации эффектов Модов извне (mods-batch.js).
// Используется чтобы новые v6-Моды могли подключаться через свой файл.
export function addModEffect(modId, handler) {
  if (typeof modId !== 'string' || typeof handler !== 'function') return;
  MOD_REGISTRY[modId] = handler;
}

// === attachMod ===
// state          — состояние матча (для лога и проверок).
// targetCard     — Card, на кого вешаем Мод. Должен быть unit, isAlive(), не sleeperState.
// modDef         — { id, name_ru }.
// source         — { ownerIdx, sourceCardId } | null.
// Возвращает { ok, mod?, overloaded?, reason? }.
//
// ВАЖНО: attachMod НЕ применяет урон Герою по прогрессии. Прогрессию вызывает
// логика розыгрыша Мода из руки в Ф3 (Б.1.c) ДО вызова attachMod.
export function attachMod(state, targetCard, modDef, source = null) {
  if (!targetCard) return { ok: false, reason: 'no_target' };
  if (targetCard.type !== 'unit') return { ok: false, reason: 'not_unit' };
  if (!targetCard.isAlive()) return { ok: false, reason: 'dead' };
  if (targetCard.sleeperState != null) return { ok: false, reason: 'sleeper' };
  if (!modDef || !modDef.id) return { ok: false, reason: 'no_def' };

  const handler = MOD_REGISTRY[modDef.id];
  let effect = null;
  if (handler) {
    effect = handler(state, targetCard);
  } else {
    if (!_warned.has(modDef.id)) {
      _warned.add(modDef.id);
      console.warn(`[mods] Мод "${modDef.id}" не зарегистрирован — игнорируется`);
    }
  }
  // null означает «заглушка» — Мод в массиве будет, но без эффекта.
  if (handler && effect == null) {
    if (!_warned.has(modDef.id)) {
      _warned.add(modDef.id);
      console.warn(`[mods] Мод "${modDef.id}" не реализован на этапе Б.1.b`);
    }
  }

  // Этап 56: стат/keyword-бонус Мода — СТРОГО из данных карты (xlsx).
  // Хэндлер выше уже отработал свои побочные эффекты (Заводской имплант +1
  // overloadThreshold и т.п.); перетираем только стат/keyword-поля effect'а
  // значениями из шаблона. "+N hp" в данных = +N к max И +N к current (с cap'ом).
  const _tpl = MOD_TPL.get(modDef.id);
  if (_tpl) {
    effect = effect || {};
    effect.atk     = _tpl.atk  || 0;
    effect.armor   = _tpl.armor || 0;
    effect.maxHp   = _tpl.hp   || 0;
    effect.hpDelta = _tpl.hp   || 0;
    effect.keywords = Array.isArray(_tpl.keywords) ? [..._tpl.keywords] : [];
    if (!effect.description) {
      effect.description = `+${effect.atk}/${effect.armor}/${effect.maxHp}` +
        (effect.keywords.length ? ` ${effect.keywords.join(',')}` : '');
    }
  }

  // Применяем дельту императивно.
  const applied = { atk: 0, armor: 0, maxHp: 0, hpDelta: 0, keywordsAdded: [] };
  if (effect) {
    if (effect.atk) {
      targetCard.current.atk += effect.atk;
      applied.atk = effect.atk;
    }
    if (effect.armor) {
      targetCard.current.armor    += effect.armor;
      targetCard.persistentArmor    = (targetCard.persistentArmor || 0)    + effect.armor;
      targetCard.persistentMaxArmor = (targetCard.persistentMaxArmor || 0) + effect.armor;
      applied.armor = effect.armor;
    }
    if (effect.maxHp) {
      // base.hp = «максимум HP» в текущей модели проекта (Танаки-passive тоже мутирует base.hp).
      targetCard.base.hp += effect.maxHp;
      applied.maxHp = effect.maxHp;
    }
    if (effect.hpDelta) {
      // Не превышаем новый maxHp.
      const newMax = targetCard.base.hp;
      const before = targetCard.current.hp;
      const after = Math.min(before + effect.hpDelta, newMax);
      targetCard.current.hp = after;
      applied.hpDelta = after - before;
    }
    // Keywords: добавляем только те, которых ещё нет; учитываем stealthUsed для 'stealth'.
    // В.1.c-2: stealthSuppressed (Нетраннер-адъютант) тоже блокирует выдачу stealth.
    if (Array.isArray(effect.keywords)) {
      for (const kw of effect.keywords) {
        if (kw === KW.STEALTH && (targetCard.stealthUsed || targetCard.stealthSuppressed)) continue;
        if (!hasKeyword(targetCard, kw)) {
          targetCard.keywords.push(kw);
          applied.keywordsAdded.push(kw);
        }
      }
    }
  }

  const mod = {
    id: modDef.id,
    name_ru: modDef.name_ru || modDef.id,
    source: source ? { ...source } : null,
    attachedAtTurn: state.currentTurn || 0,
    applied
  };
  targetCard.modifications.push(mod);

  // Лог применения.
  const desc = effect?.description || (handler ? '—' : 'неизвестный');
  state.log.add(`🔧 ${mod.name_ru} → ${targetCard.name_ru} [${ownerName(state, targetCard)}]: ${desc}`);

  // Б.1.d: Кибер-псих passive — +1 atk при наличии хотя бы одного Мода.
  kiberPsihOnModAttached(targetCard);

  // Б.2.c: Подпольный боец — первый Мод снимает блок «не может атаковать».
  podpolnyyOnModAttached(targetCard);

  // Б.1.d: Бишор trigger — Герой-владелец юнита получает +1 atk до конца матча.
  bishorOnAllyModified(state, targetCard.ownerId, targetCard);

  // Б.2.c: Хирург без лицензии — реакция ТОЛЬКО на розыгрыш карты-Мода
  // (revealPendingMods зовёт с source.isPlayed=true). На триггер-источники
  // (Раст, Нарколаборатория) — не реагирует.
  if (source && source.isPlayed && targetCard.ownerId != null) {
    hirurgOnModPlayed(state, targetCard.ownerId);
    // C.1: общий реестр mod_played-триггеров (Доктор Шрам, Бишор-лич, и др).
    fireOnModPlayed(state, targetCard, source);
  }
  // C.1: общий реестр ally_received_mod (для Уличного наёмника,
  // Лаборатории мутаций). Срабатывает на ЛЮБОЙ attach (включая trigger-
  // источники Раста/Нарколаборатории — это «союзник получил Мод»).
  if (_fireOnAllyReceivedMod) _fireOnAllyReceivedMod(state, targetCard, modDef);
  // C.1: пересчёт passive_modifier юнита (Биомех/Аддикт/Сквилл-лич +N per mod).
  if (_recomputeModCountBonus) _recomputeModCountBonus(state, targetCard);

  // Проверка Перегрузки.
  let overloaded = false;
  if (targetCard.modifications.length >= targetCard.overloadThreshold && !targetCard.overloaded) {
    targetCard.overloaded = true;
    overloaded = true;
    state.log.add(`⚠ ${targetCard.name_ru} [${ownerName(state, targetCard)}]: Перегрузка (${targetCard.modifications.length} Модов) — гибель в начале следующего хода`);
  } else if (targetCard.modifications.length >= targetCard.overloadThreshold) {
    overloaded = true;   // уже была — флаг для возврата
  }

  return { ok: true, mod, overloaded };
}

// === detachMod ===
// Снимает Мод по индексу в массиве. Откатывает дельты и пересматривает overloaded.
// Используется будущими Хирургом / Молчанием. Сейчас — только из тестов.
export function detachMod(state, targetCard, modIndex) {
  if (!targetCard || !Array.isArray(targetCard.modifications)) {
    return { ok: false, reason: 'no_target' };
  }
  if (modIndex < 0 || modIndex >= targetCard.modifications.length) {
    return { ok: false, reason: 'bad_index' };
  }
  const mod = targetCard.modifications[modIndex];
  const a = mod.applied || {};

  // Откат статов.
  if (a.atk)   targetCard.current.atk   -= a.atk;
  if (a.armor) {
    targetCard.current.armor    -= a.armor;
    targetCard.persistentArmor    = Math.max(0, (targetCard.persistentArmor || 0)    - a.armor);
    targetCard.persistentMaxArmor = Math.max(0, (targetCard.persistentMaxArmor || 0) - a.armor);
  }
  if (a.maxHp) {
    targetCard.base.hp -= a.maxHp;
    // Если current.hp выше нового max — урезаем.
    if (targetCard.current.hp > targetCard.base.hp) {
      targetCard.current.hp = targetCard.base.hp;
    }
  }
  // Откат keyword'ов: убираем только те, что мы добавили, и только если другой
  // Мод (после удаления текущего) их не предоставляет.
  if (Array.isArray(a.keywordsAdded)) {
    // Сначала уберём этот Мод из массива — потом проверим остальные.
    targetCard.modifications.splice(modIndex, 1);
    for (const kw of a.keywordsAdded) {
      const stillProvided = targetCard.modifications.some(m =>
        Array.isArray(m.applied?.keywordsAdded) && m.applied.keywordsAdded.includes(kw)
      );
      if (!stillProvided) removeKeyword(targetCard, kw);
    }
  } else {
    targetCard.modifications.splice(modIndex, 1);
  }

  // Сброс флага Перегрузки, если стало ниже порога.
  if (targetCard.overloaded && targetCard.modifications.length < targetCard.overloadThreshold) {
    targetCard.overloaded = false;
  }

  // Б.1.d: Кибер-псих — теряет +1 atk если последний Мод снят.
  kiberPsihOnModDetached(targetCard);

  // Б.2.c: Подпольный боец — последний Мод снят → снова не может атаковать.
  podpolnyyOnModDetached(targetCard);

  // C.1: пересчёт passive_modifier юнита после снятия Мода.
  if (_recomputeModCountBonus) _recomputeModCountBonus(state, targetCard);

  state.log.add(`🔧 ${mod.name_ru} снят с ${targetCard.name_ru} [${ownerName(state, targetCard)}]`);
  return { ok: true, mod };
}

// === recomputeUnitWithMods ===
// В Б.1.b — no-op (дельты применяются в attach/detach точечно).
// Сохранён как явная точка для будущих расширений (Молчание, Хирург).
// Тест T143 убедится, что вызов не ломает существующие баффы.
export function recomputeUnitWithMods(/* card */) {
  // Намеренно пусто. См. комментарий вверху файла про модель «императивных дельт».
}

// === revealPendingMods ===
// Б.1.c: раскрытие Модов в начале Ф3.
// 1) Прогрессирующий урон Герою (1→2→3→4→5+; у Сквилла отменяется, но счётчик идёт).
// 2) Поиск target по targetInstanceId. Если умер — Мод утерян.
// 3) attachMod к target (применяет эффект из реестра, проверяет Перегрузку).
// 4) Карта отправляется в discard.
// Порядок: владелец 0, потом 1; внутри — порядок размещения в Ф2.
//
// Б.2.c-fix2: страховка — Мод без основной карты этого хода НЕ разыгрывается.
// Прогрессия НЕ тикает, эффект Мода НЕ применяется. Лог: «Мод <имя> отброшен:
// нет основной карты». Срабатывает на случай рассинхрона UI/AI/движка.
export function revealPendingMods(state) {
  const queues = state.pendingMods || [[], []];
  for (let idx = 0; idx < 2; idx++) {
    const p = state.players[idx];
    const queue = queues[idx] || [];
    // Проверка: разместил ли игрок основную карту в этом ходу?
    //   - юнит на поле с placedTurn === currentTurn (только что выставлен в Ф2), ИЛИ
    //   - p.pendingAction != null (Действие ждёт резолва в Ф3).
    const placedMain = p.unitsOnField().some(u => u.placedTurn === state.currentTurn)
                    || (p.pendingAction != null);
    // Б.2.d: при активном Инициаторе у этого игрока — Моды бесплатны, прогрессия
    // не тикает. Эффект имеет TTL=2 хода с момента пробуждения Инициатора.
    const initiatorActive = !!(p.activeInitiatorEffect && p.activeInitiatorEffect.remainingTurns > 0);
    for (const item of queue) {
      const card = item.card;
      if (!placedMain) {
        state.log.add(`🔧 Мод "${card.name_ru}" отброшен: нет основной карты`);
        p.discard.push(card);
        continue;
      }
      // 1) Прогрессирующий урон Герою (отменяется при Инициаторе).
      if (initiatorActive) {
        state.log.add(
          `🚀 Инициатор [${p.name}]: Мод "${card.name_ru}" — без урона Герою и без прогрессии`
        );
      } else {
        const count = (p.modPlaysCount || 0) + 1;
        const dmg = Math.min(count, 5);
        const isSkvill = p.hero?.id === 'syn_skvill';
        if (isSkvill) {
          state.log.add(`🩸 ${shortHeroName(p.hero)} [${p.name}]: Мод #${count} → урон отменён пассивкой`);
        } else {
          // Самонанесение: цена за вторую карту, броня и иммунитет НЕ применяются
          // (это не атака врага). Парашют тоже не срабатывает — это плата игрока.
          const before = p.hero.current.hp;
          p.hero.current.hp = before - dmg;
          const after = p.hero.current.hp;
          state.log.add(`🩸 ${shortHeroName(p.hero)} [${p.name}]: Мод #${count} → Герой -${dmg} HP (HP ${before}→${after})`);
          // Б.2.c: триггер урона по Герою (Тёмный нетраннер).
          notifyCharacterDamaged(state, idx, p.hero, dmg);
        }
        // Прогрессия счётчика идёт даже у Сквилла (диздок 7.2).
        p.modPlaysCount = count;
      }

      // 2) Найти target.
      const target = findCardByInstance(state, item.targetInstanceId);
      if (!target || target.type !== 'unit' || !target.isAlive()) {
        state.log.add(`🔧 ${card.name_ru}: цель умерла, Мод утерян`);
        p.discard.push(card);
        continue;
      }

      // 3) attachMod. isPlayed=true — это розыгрыш карты-Мода игроком,
      // что важно для Хирурга (Б.2.c): он реагирует только на розыгрыш.
      attachMod(state, target, { id: card.id, name_ru: card.name_ru }, {
        ownerIdx: idx, sourceCardId: card.id, isPlayed: true
      });

      // 4) В сброс.
      p.discard.push(card);
    }
  }
  state.pendingMods = [[], []];
}

function findCardByInstance(state, instanceId) {
  if (instanceId == null) return null;
  for (const p of state.players) {
    for (let line = 0; line < 2; line++) {
      for (let slot = 0; slot < 4; slot++) {
        const u = p.field[line][slot];
        if (u && u.instanceId === instanceId) return u;
      }
    }
  }
  return null;
}

// === Б.2.a: Триггеры Модов на смерть носителя ===
// Вызывается из onUnitDied (deus/index.js) ДО dispatchUnitDied'ов внутри
// каскадных смертей. Если у carrier есть модификации с триггером на смерть —
// прогоняем их в порядке, в котором висят на юните.
//
// ВАЖНО: батарея может вызвать новые смерти (и их Посмертия), которые в свою
// очередь могут вызвать ЭТУ функцию рекурсивно — это нормально и работает
// благодаря изолированному состоянию каждого вызова.
export function applyModDeathTriggers(state, ownerIdx, deadCard) {
  if (!deadCard || !Array.isArray(deadCard.modifications)) return;
  // Снимок массива на момент входа: дальнейшие attach/detach в каскаде на других
  // юнитах не должны влиять на этот цикл.
  const mods = [...deadCard.modifications];
  for (const mod of mods) {
    switch (mod.id) {
      case BATAREYA_ID:
        batareyaOnDeath(state, ownerIdx, deadCard);
        break;
      case SERDTSE_ID:
        vtoroeSerdtseOnDeath(state, ownerIdx, deadCard);
        break;
      // Остальные Моды (Перегруз рецепторов, Завещание, Выч. мощности,
      // Механодендрит, Острый взгляд, Камуфляж) триггеров на смерть не имеют.
    }
  }
}

// === Б.2.a: Триггеры Модов на конец хода (после Ф5) ===
// Вызывается из onEndOfTurn (deus/index.js) для каждой стороны.
// Порядок per-юнит: M5 (Выч. мощности) → M4 (Завещание).
// Если Завещание убивает юнита — последующие Моды этого же юнита уже не идут.
export function applyModEndOfTurnTriggers(state, ownerIdx) {
  const p = state.players[ownerIdx];
  // Снимок живых юнитов с Модами — чтобы removeFromField (Завещание) не сдвигал итерацию.
  const carriers = [];
  for (let line = 0; line < 2; line++) {
    for (let slot = 0; slot < 4; slot++) {
      const u = p.field[line][slot];
      if (!u || !u.isAlive()) continue;
      if (!u.modifications || u.modifications.length === 0) continue;
      carriers.push(u);
    }
  }
  for (const u of carriers) {
    if (!u.isAlive()) continue;
    // M5 — выч. мощности: один раз на юнита, даже если Мод повешен дважды
    // (диздок не уточняет, но равноправный выбор «среди Модов в колоде» делает дубли
    // эквивалентными независимым роллам — оставляем как «по числу Модов»).
    const m5Count = u.modifications.filter(m => m.id === MOSCHNOSTI_ID).length;
    for (let i = 0; i < m5Count; i++) {
      vychMoschnostiEot(state, ownerIdx, u);
    }
    if (!u.isAlive()) continue;
    // M4 — Завещание: первый же убивает юнита; остальные дубли уже не сработают
    // (юнит мёртв). Все равно запускаем последовательно, чтобы первый умертвил.
    const hasZaveschanie = u.modifications.some(m => m.id === ZAVESCHANIE_ID);
    if (hasZaveschanie) {
      tsifrovoeZaveschanieEot(state, ownerIdx, u);
    }
  }
}

// === applyOverloadDeaths ===
// Вызывается в начале Ф1 ДО тяги карт. Юниты с modifications.length >=
// overloadThreshold умирают «обычной смертью», активируя Посмертие через
// onUnitDied (killer=null). Порядок: владелец 0 → 1; внутри — линия 0/слева→направо,
// потом линия 1.
export function applyOverloadDeaths(state) {
  for (let idx = 0; idx < 2; idx++) {
    const p = state.players[idx];
    // Снимок: список ссылок на юниты, чтобы removeFromField не сдвигал итерацию.
    const victims = [];
    for (let line = 0; line < 2; line++) {
      for (let slot = 0; slot < 4; slot++) {
        const u = p.field[line][slot];
        if (!u) continue;
        if (!u.isAlive()) continue;
        if (u.modifications && u.modifications.length >= u.overloadThreshold) {
          victims.push(u);
        }
      }
    }
    for (const u of victims) {
      const count = u.modifications.length;
      state.log.add(`⚠ ${u.name_ru} [${p.name}] погибает от Перегрузки (${count} Модов)`);
      // Стандартный path смерти.
      u.alive = false;
      p.removeFromField(u);
      onUnitDied(state, {
        ownerIdx: idx, card: u,
        killer: null, killerIdx: null, killerWasCounter: false
      });
    }
  }
}
